import { FunctionComponent, PropsWithChildren, ReactElement } from "react";
import { Link as MuiLink, LinkProps, Box } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon/SvgIcon";
import {
  CustomImageIcon,
  JupyterLabsIcon,
  RStudioProIcon,
  RStudioIcon,
  SASIcon,
  StreamlitIcon,
  CelldegaIcon,
  OmicsIcon,
} from "~/assets/icons";

const Code = (props: PropsWithChildren) => (
  <Box
    className="code"
    sx={{
      display: "inline-block",
      fontFamily: "monospace",
      px: 0.5,
      my: 0.25,
      color: "code.main",
      backgroundColor: "grey.100",
    }}
    {...props}
  />
);

const Caption = (props: PropsWithChildren) => (
  <Box sx={{ fontSize: "0.75rem", ml: 0.5, mt: 1 }} {...props} />
);

const Link = (props: LinkProps) => (
  <MuiLink target="_blank" rel="noreferrer" underline="hover" {...props} />
);

enum ImageType {
  CUSTOM = "custom",
  JUPYTER = "jupyter",
  JUPYTER_CLAUDE_CODE = "jupyter_claude_code",
  JUPYTER_DL = "jupyter_dl",
  R_STUDIO = "rstudio",
  POSIT_WORKBENCH = "posit_workbench",
  SAS = "sas",
  STREAMLIT_DEMO = "streamlit_demo",
  CELLDEGA = "celldega",
  OMICS = "omics",
}

// Scoped to image types with kernel-activity polling -- only these can be
// told idle from busy. Empty list = no restriction. Mirror changes in
// terminate_ec2_instances.py's AUTO_STOP_SCOPED_IMAGE_TYPES.
export const AUTO_STOP_SCOPED_IMAGE_TYPES: ImageType[] = [
  ImageType.JUPYTER,
  ImageType.JUPYTER_CLAUDE_CODE,
  ImageType.JUPYTER_DL,
  ImageType.CELLDEGA,
  ImageType.OMICS,
];

export const DISPLAY_COMPUTE_IMAGE: Record<
  ImageType,
  {
    label: string;
    icon: typeof SvgIcon | FunctionComponent;
    description: string | ReactElement;
    caption?: string | ReactElement | undefined;
  }
> = {
  [ImageType.CUSTOM]: {
    label: "Custom Image",
    icon: CustomImageIcon,
    description: "Add your own custom image",
  },
  [ImageType.JUPYTER]: {
    label: "Jupyter - Scipy Notebook",
    icon: JupyterLabsIcon,
    description: (
      <Box>
        Jupyter notebook that includes popular packages from the scientific Python ecosystem and
        common utilities like <Code>curl</Code>, <Code>git</Code>, <Code>vim</Code>, etc. This is
        the <Code>jupyter/datascience-notebook</Code> image maintained by the Jupyter project team.
      </Box>
    ),
    caption: (
      <Caption>
        See this{" "}
        <Link href="https://jupyter-docker-stacks.readthedocs.io/en/latest/using/selecting.html#jupyter-scipy-notebook">
          link
        </Link>{" "}
        for more information.{" "}
      </Caption>
    ),
  },
  [ImageType.JUPYTER_CLAUDE_CODE]: {
    label: "Jupyter - Scipy + Claude Code",
    icon: JupyterLabsIcon,
    description: (
      <Box>
        Jupyter SciPy notebook with Claude Code installed and configured to use Amazon Bedrock. Each
        environment gets its own dedicated Bedrock inference profile for cost tracking.
      </Box>
    ),
    caption: (
      <Caption>
        Claude Code is configured to use Claude Sonnet 5 via Bedrock with the environment&apos;s IAM
        role.
      </Caption>
    ),
  },
  [ImageType.JUPYTER_DL]: {
    label: "Jupyter - Deep Learning",
    icon: JupyterLabsIcon,
    description: (
      <Box>
        Jupyter notebook that includes popular packages from the scientific Python ecosystem and
        CUDA accelerated <Code>pytorch</Code>. This is the <Code>jupyter/pytorch-notebook</Code>{" "}
        image maintained by the Jupyter project team.
      </Box>
    ),
    caption: (
      <Caption>
        See this{" "}
        <Link href="https://jupyter-docker-stacks.readthedocs.io/en/latest/using/selecting.html#jupyter-pytorch-notebook">
          link
        </Link>{" "}
        for more information.{" "}
      </Caption>
    ),
  },
  [ImageType.R_STUDIO]: {
    label: "RStudio - Rocker",
    icon: RStudioIcon,
    description: (
      <Box>
        RStudio including the popular <Code>tidyverse</Code> package set for data science. This is
        the rocker/tidyverse image maintained by the Rocker project team.
      </Box>
    ),
    caption: (
      <Caption>
        See <Link href="https://rocker-project.org/images/versioned/rstudio.html">link</Link> for
        more information. Trademark above unmodified, © 2016 The R Foundation, used under CC-BY-SA
        4.0 with warranties disclaimed, available{" "}
        <Link href="https://www.r-project.org/logo/">here</Link>.
      </Caption>
    ),
  },
  [ImageType.POSIT_WORKBENCH]: {
    label: "RStudio Pro",
    icon: RStudioProIcon,
    description: (
      <Box>
        RStudio Pro including a pre-installed standard epidemiology and bioinformatics toolset. This
        is the rstudio/rstudio-workbench image maintained by the Posit Workbench team.
      </Box>
    ),
    caption: (
      <Caption>
        See <Link href="https://posit.co/products/enterprise/workbench/">Posit Workbench</Link> for
        more information.
      </Caption>
    ),
  },
  [ImageType.SAS]: {
    label: "SAS Studio [Testing Purposes Only]",
    icon: SASIcon,
    description: (
      <Box>
        A comprehensive suite of SAS software components for statistical analysis, predictive
        modeling, and data visualization. Maintained by the SAS Analytics team.
      </Box>
    ),
    caption: (
      <Caption>
        See <Link href="https://www.sas.com/en_us/home.html">SAS</Link> for more information.
      </Caption>
    ),
  },
  [ImageType.STREAMLIT_DEMO]: {
    label: "Streamlit - Manifold Demo",
    icon: StreamlitIcon,
    description: (
      <Box>
        Multi-page Streamlit application that is running the various demos for the Science Cloud.
      </Box>
    ),
    caption: (
      <Caption>
        See the repo at <Link href="https://github.com/manifoldai/sc-demo">sc-demo</Link> for more
        information.
      </Caption>
    ),
  },
  [ImageType.CELLDEGA]: {
    label: "Celldega",
    icon: CelldegaIcon,
    description: (
      <Box>
        Celldega is a web-based application for visualizing and analyzing single-cell RNA-seq data.
        It provides an interactive interface for exploring gene expression patterns, clustering
        cells, and performing differential expression analysis.
      </Box>
    ),
    caption: (
      <Caption>
        See <Link href="https://github.com/broadinstitute/celldega">Celldega</Link> for more
        information.
      </Caption>
    ),
  },
  [ImageType.OMICS]: {
    label: "WDL and Nextflow on AWS HealthOmics",
    icon: OmicsIcon,
    description: (
      <Box>
        A JupyterLab-based image for running WDL and Nextflow using the AWS HealthOmics service.
        Includes the AWS HealthOmics CLI and a Manifold-authored extension to simplify interactions
        with the AWS HealthOmics service.
      </Box>
    ),
    caption: (
      <Caption>
        See{" "}
        <Link href="https://docs.aws.amazon.com/omics/latest/dev/what-is-healthomics.html">
          HealthOmics
        </Link>{" "}
        for more information.
      </Caption>
    ),
  },
};

export default ImageType;
